import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Search, User, Briefcase, Loader2 } from "lucide-react";

export default function SearchDialog({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ jobs: [], customers: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const doSearch = useCallback(async (term) => {
    if (term.length < 2) { setResults({ jobs: [], customers: [] }); setError(false); return; }
    setLoading(true);
    setError(false);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(term)}`);
      setResults(res.data);
    } catch (e) {
      console.error("Search failed:", e);
      setError(true);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(q), 300);
    return () => clearTimeout(t);
  }, [q, doSearch]);

  const go = (path) => { onOpenChange(false); setQ(""); navigate(path); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden" data-testid="global-search-dialog">
      <DialogTitle className="sr-only">Search</DialogTitle>
      <DialogDescription className="sr-only">Search for jobs and customers</DialogDescription>
        <div className="flex items-center gap-2 border-b px-4">
          {loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : <Search className="h-4 w-4 text-muted-foreground" />}
          <Input
            data-testid="global-search-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search job no, customer, mobile, article, finish…"
            className="border-0 focus-visible:ring-0 h-12"
          />
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {results.customers.length > 0 && (
            <div className="mb-2">
              <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">Customers</p>
              {results.customers.map((c) => (
                <button key={c.id} data-testid={`search-customer-${c.id}`} onClick={() => go(`/customers/${c.id}`)}
                  className="w-full flex items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-accent text-left transition-colors">
                  <User className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{c.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{c.mobile}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {results.jobs.length > 0 && (
            <div>
              <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">Jobs</p>
              {results.jobs.map((j) => (
                <button key={j.id} data-testid={`search-job-${j.id}`} onClick={() => go(`/jobs/${j.id}`)}
                  className="w-full flex items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-accent text-left transition-colors">
                  <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{j.job_number} · {j.article_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{j.customer_name} · {(j.required_finishes || []).join(", ")}</p>
                  </div>
                  <StatusBadge status={j.status} />
                </button>
              ))}
            </div>
          )}
          {q.length >= 2 && !loading && error && (
            <p className="p-4 text-sm text-destructive text-center" data-testid="search-error">Search failed. Please try again.</p>
          )}
          {q.length >= 2 && !loading && !error && results.jobs.length === 0 && results.customers.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground text-center" data-testid="search-no-results">No results for "{q}"</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
